import { useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DeleteIcon } from 'src/assets/tailwind/delete.svg';
import { ReactComponent as XIcon } from 'src/assets/tailwind/x-close.svg';
import { Button } from 'src/components/ui/button';
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'src/components/ui/dialog';
import { RHFInput } from 'src/components/ui/RHF/RHFInput';
import { RHFTextarea } from 'src/components/ui/RHF/RHFTextarea';
import { Tab } from 'src/components/ui/tab';
import { Typography } from 'src/components/ui/typography';
import { ConfirmationDialog } from 'src/components/ui/ConfirmationDialog';
import type { DataLibraryWebLinkValues } from 'src/content/DataLibrary/schemas';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import { useWebLinkManager } from './useWebLinkManager';

interface TabItem {
  text: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

type DataLibraryWebLinkProps = {
  tabs: TabItem[];
  toggleUploadModal: () => void;
};

export default function DataLibraryWebLink({
  toggleUploadModal = () => {},
  tabs = []
}: DataLibraryWebLinkProps) {
  const { t } = useTranslation();
  const {
    methods,
    fields,
    removeLink,
    onSubmit,
    resetForm,
    handleInputChange,
    isUploading,
    linkCount
  } = useWebLinkManager({
    onSuccess: () => {
      toggleUploadModal();
    }
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const formValues = methods.watch();
  const hasData =
    formValues.collectionName?.trim().length > 0 ||
    formValues.links?.some((link) => link.url?.trim().length > 0);

  const handleClose = () => {
    resetForm();
    toggleUploadModal();
  };

  const attemptAction = (action: () => void, e?: React.MouseEvent | Event) => {
    if (e) {
      e.preventDefault();
    }
    if (hasData) {
      pendingActionRef.current = action;
      setShowConfirm(true);
    } else {
      action();
    }
  };

  const attemptClose = (e?: React.MouseEvent | Event) =>
    attemptAction(handleClose, e);

  const confirmAbort = () => {
    setShowConfirm(false);
    const action = pendingActionRef.current ?? handleClose;
    pendingActionRef.current = null;
    action();
  };

  return (
    <>
      <DialogContent
        hideDefaultClose
        onPointerDownOutside={attemptClose}
        onEscapeKeyDown={attemptClose}
      >
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <DialogHeader className="tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center">
              <DialogTitle>{t(T.uploadFiles)}</DialogTitle>
              <Button
                leftIcon={XIcon}
                size="small"
                hierarchy="ghost"
                iconOnly
                className="tw:w-2xl"
                onClick={attemptClose}
              />
            </DialogHeader>

            <div className="tw:flex tw:px-2xl tw:py-s tw:border-b tw:border-b-border-secondary">
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  {...tab}
                  onClick={() => attemptAction(tab.onClick)}
                />
              ))}
            </div>

            <div className="tw:p-2xl tw:h-[432px] tw:flex tw:flex-col tw:gap-l">
              <RHFInput<DataLibraryWebLinkValues>
                name="collectionName"
                label={t(T.nameCollection)}
                placeholder={t(T.nameCollection)}
              />

              <div className="tw:flex tw:flex-col tw:gap-s  tw:overflow-y-auto ">
                <div className="tw:flex tw:items-center tw:justify-between">
                  <Typography
                    variant="label"
                    colorClass="tw:text-text-secondary"
                  >
                    {t(T.webLinks)}
                  </Typography>
                  <Typography
                    variant="label"
                    colorClass="tw:text-text-secondary"
                  >
                    {t(T.addedCountOf5, { count: linkCount })}
                  </Typography>
                </div>
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="tw:flex tw:items-center  tw:gap-s"
                  >
                    <span className="tw:w-full tw:p-0.5">
                      <RHFTextarea<DataLibraryWebLinkValues>
                        name={`links.${index}.url` as const}
                        placeholder={t(T.addWebLink)}
                        className="tw:max-h-[112px] tw:overflow-y-auto"
                        maxWords={500}
                        onChange={(e) => {
                          handleInputChange(index, e.target.value);
                        }}
                      />
                    </span>
                    {fields.length > 1 && (
                      <Button
                        iconOnly
                        leftIcon={DeleteIcon}
                        onClick={() => removeLink(index)}
                        hierarchy="secondary"
                      />
                    )}
                  </div>
                ))}

                {methods?.formState?.errors?.links?.root ? (
                  <Typography variant="h6" className="tw:text-text-error">
                    {methods.formState.errors.links.root.message}
                  </Typography>
                ) : null}
              </div>
            </div>
            <DialogFooter>
              <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
                <Button
                  hierarchy="destructive"
                  size="small"
                  text={t(T.cancel)}
                  onClick={attemptClose}
                />
                <Button
                  hierarchy="primary"
                  text={isUploading ? t(T.uploadingEllipsis) : t(T.add)}
                  size="small"
                  loading={isUploading}
                  onClick={(e) => {
                    e.preventDefault();
                    onSubmit();
                  }}
                />
              </div>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
      <ConfirmationDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmAbort}
        title={t(T.abortUpload)}
        description={t(T.abortUploadDescription)}
        confirmText={t(T.yesAbortAll)}
        cancelText={t(T.noKeepUploading)}
      />
    </>
  );
}
